import React, { useRef, useMemo } from "react";

import { Text, Group, Rect } from "react-konva";
import Konva from "konva";

import {
  Paragraph,
  SubtitleStyles,
  BackgroundStyle,
  LetterCase,
} from "../../types";

interface SubtitleRendererProps {
  paragraph: Paragraph;
  styles: SubtitleStyles;
  currentTimeInMilliseconds: number;
  videoDimensions: { width: number; height: number };
  handlePositionX?: number;
  handlePositionY?: number;
  handleWidth?: number;
}

export default function SubtitleRenderer({
  paragraph,
  styles,
  currentTimeInMilliseconds,
  videoDimensions,
  handlePositionX,
  handlePositionY,
  handleWidth,
}: SubtitleRendererProps) {
  const textRef = useRef<Konva.Text>(null);
  const rectRef = useRef<Konva.Rect>(null);
  const groupRef = useRef<Konva.Group>(null);

  const paragraphStartMs = paragraph.start * 1000;
  const paragraphEndMs = paragraph.end * 1000;
  const isCurrentParagraph =
    currentTimeInMilliseconds >= paragraphStartMs &&
    currentTimeInMilliseconds <= paragraphEndMs;

  // Process text based on letter case - make it reactive to paragraph text changes
  const displayText = useMemo(() => {
    let processedText = paragraph.text;
    switch (styles.letter_case) {
      case LetterCase.UPPERCASE:
        processedText = processedText.toUpperCase();
        break;
      case LetterCase.LOWERCASE:
        processedText = processedText.toLowerCase();
        break;
      case LetterCase.CAPITALIZE:
        processedText = processedText.replace(/\b\w/g, (char) =>
          char.toUpperCase(),
        );
        break;
      default:
        break;
    }
    return processedText;
  }, [paragraph.text, styles.letter_case]);

  // Calculate positions using real-time handle position when available, otherwise use saved styles
  const centerX =
    ((handlePositionX ?? styles.text_handle_position_x_percentage ?? 50) /
      100) *
    videoDimensions.width;
  const y =
    ((handlePositionY ?? styles.text_handle_position_y_percentage ?? 80) /
      100) *
    videoDimensions.height;
  const width =
    ((handleWidth ?? styles.text_handle_width_percentage ?? 80) / 100) *
    videoDimensions.width;

  // Position the group at the center
  const groupX = centerX;

  // Calculate background height to match the blue box
  const getBackgroundHeight = () => {
    const baseSize = styles?.size || 24;
    const lineHeight = styles?.line_height || 1.2;
    const padding = 40; // Account for text padding (20px top + 20px bottom)

    // Estimate height based on text length and width
    const textLength = paragraph.text.length;
    const boxWidth = width;
    const charsPerLine = Math.floor((boxWidth - 40) / (baseSize * 0.6)); // Rough estimate
    const estimatedLines = Math.max(1, Math.ceil(textLength / charsPerLine));
    return Math.max(
      60,
      Math.floor(baseSize * lineHeight * estimatedLines) + padding,
    );
  };

  if (!isCurrentParagraph) return null;

  return (
    <Group
      ref={groupRef}
      x={groupX}
      y={y}
      opacity={1}
      key={`${paragraph.id}-${paragraph.text}`}
    >
      <Rect
        ref={rectRef}
        x={0}
        y={0}
        width={width}
        height={getBackgroundHeight()}
        fill={
          styles?.background_style !== BackgroundStyle.NONE
            ? styles?.background_color || "black"
            : "transparent"
        }
        opacity={0.8}
        cornerRadius={
          styles?.background_style === BackgroundStyle.ROUNDED ? 10 : 0
        }
      />
      <Text
        ref={textRef}
        x={0}
        y={0}
        text={displayText}
        width={width}
        fontSize={styles?.size || 24}
        fill={styles?.color || "white"}
        fontFamily={styles?.font}
        fontStyle={(() => {
          if (styles?.italic && styles?.bold) return "italic bold";
          if (styles?.italic) return "italic";
          if (styles?.bold) return "bold";
          return "normal";
        })()}
        textDecoration={styles?.underline ? "underline" : ""}
        lineHeight={styles?.line_height}
        letterSpacing={styles?.letter_spacing}
        align={styles?.text_align || "center"}
        verticalAlign="middle"
        stroke={styles?.outline_color}
        strokeWidth={styles?.outline_thickness}
        wrap="word"
        padding={20}
        listening={false}
      />
    </Group>
  );
}
